import { Button, HTMLTable, Icon, Tooltip } from "@blueprintjs/core";
import classnames from "classnames";
import ConstraintExpandPanel, {
    ConstraintDetailEntry
} from "components/busPage/ConstraintExpandPanel";
import { getAllSizeLabels, LabelStyleType } from "components/common/labels";
import React, { useCallback, useState } from "react";
import { Constraint } from "types/constraintType";
import { GeneratorType } from "types/generatorType";
import { LineTapConstraint } from "types/lineConstraintType";
import { Loading } from "types/loadingType";
import {
    RelatedProjectsByConstraint,
    relatedProjectsConstraintKey
} from "types/queueProjectRelatednessType";
import { PowerAmounts } from "../BusDetailPane";
import {
    getConstraintsOutsideSize,
    getRelevantConstraintsForSize
} from "../constraintsHelpers";
import "./ConstraintsDetailsDialogTable.scss";
import {
    CONSTRAINT_TABLE_COLUMNS,
    getCompactHiddenColumns,
    getConfiguredColumns,
    getConstraintsTableHeader,
    RenderConstraintTableValueParams
} from "./constraintsTableHelpers";

export interface ConstraintTableConfig {
    hideAllocatedCosts: boolean;
    hideTotalCosts: boolean;
    showPostStudyLoad: boolean;
    // Used for ERCOT
    maybeGetModelName?: (constraint: Constraint | LineTapConstraint) => string;
    showContingency: boolean;
    // ERCOT-only: show "Contingency Type" + "Contingency Definition" columns.
    showContingencyDetails?: boolean;
    generatorType: GeneratorType;
    missingBranchMetadata: boolean;
    hideResourceType: boolean;
    // Duke-only: show "MW impact" (MW impact / MVA rating) alongside Dfax.
    showImpactPercent?: boolean;
    // Bus page: hide the detail columns (resource type, loads, element
    // type, line length, rating, contingency) from the table and render
    // them inside each row's expand panel instead, so the table fits the
    // main column without horizontal crushing. Rows become expandable
    // even without related-projects data. CSV export configs should NOT
    // set this — exports keep the full column set.
    compactColumns?: boolean;
    // Adds a "Related projects" count column. Set internally by
    // ConstraintDetailsDialogTable whenever relatedProjectsByConstraint
    // is provided — callers should not set it themselves.
    showRelatedProjectsCount?: boolean;
}
type ConstraintDetailsDialogTableProps = {
    readonly constraints: Loading<
        ReadonlyArray<Constraint | LineTapConstraint>
    >;
    readonly powerAmounts: PowerAmounts;
    readonly showAllConstraints: boolean;
    readonly showCapacityLabel: boolean;
    readonly showChargingLabel: boolean;
    readonly labelStyle: LabelStyleType;
    readonly constraintTableConfig: ConstraintTableConfig;
    /**
     * Optional per-constraint relatedness map. When provided, each row that
     * has matching related-project data becomes expandable: a leading
     * chevron column is added and clicking the row opens an inline
     * `ConstraintExpandPanel` with the ranked project list.
     */
    readonly relatedProjectsByConstraint?: RelatedProjectsByConstraint;
    // Defaults to striped; the bus page opts out for a cleaner look.
    readonly striped?: boolean;
    // Defaults to no row separators; the bus page opts in (hairlines
    // between rows instead of stripes). Custom rather than Blueprint's
    // `bordered`, which also draws unwanted column borders.
    readonly rowBorders?: boolean;
};

const ConstraintDetailsDialogTable: React.FC<
    ConstraintDetailsDialogTableProps
> = (props) => {
    const {
        constraints,
        powerAmounts,
        showAllConstraints,
        showCapacityLabel,
        showChargingLabel,
        labelStyle,
        constraintTableConfig,
        relatedProjectsByConstraint,
        striped = true,
        rowBorders = false
    } = props;
    const [furtherConstraintsOpen, setFurtherConstraintsOpen] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState<ReadonlyArray<string>>([]);
    const toggleExpand = useCallback((key: string) => {
        setExpandedKeys((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    }, []);

    const isExpandable =
        relatedProjectsByConstraint !== undefined ||
        constraintTableConfig.compactColumns === true;
    // The "Related projects" count column tracks the data: it appears
    // exactly when relatedness data was provided, so regions without
    // related queue projects never show a misleading empty column.
    const effectiveTableConfig: ConstraintTableConfig = {
        ...constraintTableConfig,
        showRelatedProjectsCount: relatedProjectsByConstraint !== undefined
    };
    const headerColumns = getHeaderColumns(effectiveTableConfig, isExpandable);

    const numColumns = headerColumns.length;

    return (
        <HTMLTable
            compact={true}
            interactive={false}
            striped={striped}
            className={classnames("ConstraintDetailsDialogTable-table", {
                "ConstraintDetailsDialogTable-row-borders": rowBorders
            })}
        >
            <thead className="ConstraintDetailsDialogTable-header">
                <tr>{headerColumns}</tr>
            </thead>
            {constraints !== "loading" &&
                getTableBody(
                    constraints,
                    powerAmounts,
                    furtherConstraintsOpen,
                    setFurtherConstraintsOpen,
                    showAllConstraints,
                    showCapacityLabel,
                    showChargingLabel,
                    labelStyle,
                    numColumns,
                    effectiveTableConfig,
                    relatedProjectsByConstraint,
                    expandedKeys,
                    toggleExpand
                )}
        </HTMLTable>
    );
};

const getTableBody = (
    constraints: ReadonlyArray<Constraint | LineTapConstraint>,
    powerAmounts: PowerAmounts,
    furtherConstraintsOpen: boolean,
    setFurtherConstraintsOpen: (isOpen: boolean) => void,
    showAllConstraints: boolean,
    showCapacityLabel: boolean,
    showChargingLabel: boolean,
    labelStyle: LabelStyleType,
    numColumns: number,
    constraintTableConfig: ConstraintTableConfig,
    relatedProjectsByConstraint: RelatedProjectsByConstraint | undefined,
    expandedKeys: ReadonlyArray<string>,
    toggleExpand: (key: string) => void
): JSX.Element => {
    if (showAllConstraints) {
        return (
            <tbody>
                {getConstraintRows({
                    constraintRows: constraints,
                    powerAmounts,
                    withinSize: false,
                    labelStyle,
                    constraintTableConfig,
                    relatedProjectsByConstraint,
                    expandedKeys,
                    toggleExpand,
                    numColumns
                })}
            </tbody>
        );
    } else {
        return (
            <tbody>
                {getConstraintRowsWithinSize({
                    constraints,
                    powerAmounts,
                    showChargingLabel,
                    showCapacityLabel,
                    labelStyle,
                    numColumns,
                    constraintTableConfig,
                    relatedProjectsByConstraint,
                    expandedKeys,
                    toggleExpand
                })}
                {getFurtherConstraintsOpenRow(
                    furtherConstraintsOpen,
                    setFurtherConstraintsOpen,
                    powerAmounts,
                    showCapacityLabel,
                    showChargingLabel,
                    labelStyle,
                    numColumns
                )}
                {furtherConstraintsOpen &&
                    getConstraintsRowsOutsideSize({
                        constraints,
                        powerAmounts,
                        labelStyle,
                        constraintTableConfig,
                        relatedProjectsByConstraint,
                        expandedKeys,
                        toggleExpand,
                        numColumns
                    })}
            </tbody>
        );
    }
};

const getFurtherConstraintsOpenRow = (
    furtherConstraintsOpen: boolean,
    setFurtherConstraintsOpen: (isOpen: boolean) => void,
    powerAmounts: PowerAmounts,
    showCapacityLabel: boolean,
    showChargingLabel: boolean,
    labelStyle: LabelStyleType,
    numColumns: number
): JSX.Element => {
    const sizingLabel = getAllSizeLabels(
        powerAmounts,
        showCapacityLabel,
        showChargingLabel,
        labelStyle
    );
    return (
        <tr key="expansion-button">
            <td
                colSpan={numColumns}
                className="ConstraintDetailsDialogTable-further-constraints-td"
            >
                <Button
                    minimal
                    icon={
                        furtherConstraintsOpen
                            ? "chevron-down"
                            : "chevron-right"
                    }
                    onClick={() =>
                        setFurtherConstraintsOpen(!furtherConstraintsOpen)
                    }
                >
                    {furtherConstraintsOpen
                        ? `Hide constraints beyond ${sizingLabel}`
                        : `Show constraints beyond ${sizingLabel}`}
                </Button>
            </td>
        </tr>
    );
};

const getConstraintRowsWithinSize = (props: {
    constraints: ReadonlyArray<Constraint | LineTapConstraint>;
    powerAmounts: PowerAmounts;
    showChargingLabel: boolean;
    showCapacityLabel: boolean;
    labelStyle: LabelStyleType;
    numColumns: number;
    constraintTableConfig: ConstraintTableConfig;
    relatedProjectsByConstraint: RelatedProjectsByConstraint | undefined;
    expandedKeys: ReadonlyArray<string>;
    toggleExpand: (key: string) => void;
}): ReadonlyArray<JSX.Element> | JSX.Element => {
    const {
        constraints,
        powerAmounts,
        showCapacityLabel,
        showChargingLabel,
        labelStyle,
        numColumns,
        constraintTableConfig,
        relatedProjectsByConstraint,
        expandedKeys,
        toggleExpand
    } = props;
    const constraintsWithinSize = getRelevantConstraintsForSize(
        constraints,
        powerAmounts
    );
    const sizingLabel = getAllSizeLabels(
        powerAmounts,
        showCapacityLabel,
        showChargingLabel,
        labelStyle
    );

    if (constraintsWithinSize.length === 0) {
        return (
            <tr key="no-rows">
                <td colSpan={numColumns}>
                    <div className="ConstraintDetailsDialogTable-no-constraints-required-row">
                        No constraints triggered @ {sizingLabel}
                    </div>
                </td>
            </tr>
        );
    }

    return getConstraintRows({
        constraintRows: constraintsWithinSize,
        powerAmounts,
        withinSize: true,
        labelStyle,
        constraintTableConfig,
        relatedProjectsByConstraint,
        expandedKeys,
        toggleExpand,
        numColumns
    });
};

const getConstraintsRowsOutsideSize = (props: {
    constraints: ReadonlyArray<Constraint | LineTapConstraint>;
    powerAmounts: PowerAmounts;
    labelStyle: LabelStyleType;
    constraintTableConfig: ConstraintTableConfig;
    relatedProjectsByConstraint: RelatedProjectsByConstraint | undefined;
    expandedKeys: ReadonlyArray<string>;
    toggleExpand: (key: string) => void;
    numColumns: number;
}): ReadonlyArray<JSX.Element> => {
    const {
        constraints,
        powerAmounts,
        labelStyle,
        constraintTableConfig,
        relatedProjectsByConstraint,
        expandedKeys,
        toggleExpand,
        numColumns
    } = props;

    const constraintsOutsideSize = getConstraintsOutsideSize(
        constraints,
        powerAmounts
    );

    return getConstraintRows({
        constraintRows: constraintsOutsideSize,
        powerAmounts,
        withinSize: false,
        labelStyle,
        constraintTableConfig,
        relatedProjectsByConstraint,
        expandedKeys,
        toggleExpand,
        numColumns
    });
};

const getConstraintRows = (props: {
    constraintRows: ReadonlyArray<Constraint | LineTapConstraint>;
    powerAmounts: PowerAmounts;
    withinSize: boolean;
    labelStyle: LabelStyleType;
    constraintTableConfig: ConstraintTableConfig;
    relatedProjectsByConstraint: RelatedProjectsByConstraint | undefined;
    expandedKeys: ReadonlyArray<string>;
    toggleExpand: (key: string) => void;
    numColumns: number;
}): ReadonlyArray<JSX.Element> => {
    const {
        constraintRows,
        powerAmounts,
        withinSize,
        labelStyle,
        constraintTableConfig,
        relatedProjectsByConstraint,
        expandedKeys,
        toggleExpand,
        numColumns
    } = props;
    const isExpandable =
        relatedProjectsByConstraint !== undefined ||
        constraintTableConfig.compactColumns === true;
    // Detail columns removed from the table by compact mode; rendered
    // inside each expanded row instead.
    const compactHiddenColumns = getCompactHiddenColumns(constraintTableConfig);
    return [...constraintRows]
        .sort((a, b) => a.injectionThreshold - b.injectionThreshold)
        .flatMap((constraint) => {
            const renderParams: RenderConstraintTableValueParams = {
                constraint,
                powerAmounts,
                labelStyle,
                withinSize,
                constraintTableConfig,
                relatedProjectsByConstraint
            };
            const rowValues = constraintToRow(renderParams);
            const constraintDetails: ReadonlyArray<ConstraintDetailEntry> =
                compactHiddenColumns.map((column) => ({
                    label: column.header,
                    value: column.renderValue(renderParams)
                }));
            const relatedProjectsKey = relatedProjectsConstraintKey(
                constraint.monitoredFacilityId,
                constraint.contingency,
                constraint.resourceType
            );
            // The related-projects key alone is not unique here: in ERCOT
            // the same facility/contingency/resource type appears once per
            // model (e.g. Summer Peak and HRML scope views). Include the
            // scope view so React keys stay unique and rows reconcile
            // correctly when model checkboxes are toggled.
            const rowKey = `${relatedProjectsKey}|${constraint.scopeView}`;

            if (!isExpandable) {
                return [<tr key={rowKey}>{rowValues}</tr>];
            }

            const projects =
                relatedProjectsByConstraint?.get(relatedProjectsKey);
            const hasProjects = projects !== undefined && projects.length > 0;
            const hasExpandContent =
                hasProjects || constraintDetails.length > 0;
            const isExpanded =
                hasExpandContent && expandedKeys.includes(rowKey);

            const chevronCell = hasExpandContent ? (
                <td className="ConstraintDetailsDialogTable-chevron-td">
                    <Icon
                        icon={isExpanded ? "chevron-down" : "chevron-right"}
                        className="ConstraintDetailsDialogTable-chevron-icon"
                    />
                </td>
            ) : (
                <td className="ConstraintDetailsDialogTable-chevron-td" />
            );

            const dataRow = (
                <tr
                    key={rowKey}
                    className={classnames({
                        "ConstraintDetailsDialogTable-expandable-row":
                            hasExpandContent,
                        "ConstraintDetailsDialogTable-expanded-row": isExpanded
                    })}
                    onClick={
                        hasExpandContent
                            ? () => toggleExpand(rowKey)
                            : undefined
                    }
                >
                    {chevronCell}
                    {rowValues}
                </tr>
            );

            if (!isExpanded) {
                return [dataRow];
            }
            return [
                dataRow,
                <tr key={`${rowKey}-expanded`}>
                    <td
                        colSpan={numColumns}
                        className="ConstraintDetailsDialogTable-expand-td"
                    >
                        <ConstraintExpandPanel
                            // Coalesce to an empty array whenever
                            // relatedness data exists at all, so the panel
                            // can render an explicit "(0) / none" section
                            // instead of omitting it. Stays undefined when
                            // the page has no relatedness data (e.g.
                            // non-MISO), where a zero would be misleading.
                            projects={
                                relatedProjectsByConstraint !== undefined
                                    ? projects ?? []
                                    : undefined
                            }
                            details={constraintDetails}
                        />
                    </td>
                </tr>
            ];
        });
};

/**
 * Converts a single constraint to a list of cell values representing a single row in the constraint table.
 */
const constraintToRow = (
    params: RenderConstraintTableValueParams
): JSX.Element[] => {
    const configuredColumns = getConfiguredColumns(
        CONSTRAINT_TABLE_COLUMNS,
        params.constraintTableConfig
    );

    return configuredColumns.map((column, index) => {
        const stringValue = column.renderValue(params);
        const ellipsized = column.ellipsizedColumn;
        const content = (
            <div
                className={classnames({
                    "ConstraintDetailsDialogTable-ellipsize": ellipsized
                })}
            >
                {stringValue}
            </div>
        );
        return (
            <td key={index}>
                {ellipsized ? (
                    <Tooltip content={stringValue} placement="left">
                        {content}
                    </Tooltip>
                ) : (
                    content
                )}
            </td>
        );
    });
};

const getHeaderColumns = (
    config: ConstraintTableConfig,
    isExpandable: boolean
) => {
    const labels = getConstraintsTableHeader(config);
    const labelCells = labels.map((label) => (
        <th key={label} className="ConstraintDetailsDialogTable-header-th">
            {label}
        </th>
    ));

    if (!isExpandable) {
        return labelCells;
    }
    return [
        <th
            key="expand-chevron"
            className="ConstraintDetailsDialogTable-chevron-th"
        />,
        ...labelCells
    ];
};

export default ConstraintDetailsDialogTable;
